<template>
  <div>
    <v-form ref="form" v-model="valid">
      <v-select
        v-model="localValue.type"
        :items="threatTypeOptions"
        :rules="[required()]"
        class="mb-6 mt-4"
        variant="outlined"
        :placeholder="$t('modals.addThreatType.threatType.label')"
        :label="$t('modals.addThreatType.threatType.label')"
        :menu-props="{ width: 500 }"
      >
        <template #item="{ item, props, index }">
          <v-list-item v-bind="props" title="">
            <span class="subtitle2">{{ item.raw.name }}</span>
            <div class="text-indigo-medium">{{ item.raw.description }}</div>
          </v-list-item>
          <v-divider v-if="index !== threatTypeOptions.length - 1" class="mt-2 mb-2"></v-divider>
        </template>
        <template #selection="{ item }">
          {{ item.raw.name }}
        </template>
      </v-select>

      <v-divider></v-divider>

      <div class="subtitle2 mt-8 mb-4">
        {{ $t("modals.addAccessPermissions.remediation.title") }}
      </div>
      <v-select
        v-model="localValue.remediation"
        :items="remediationOptions"
        data-testid="access-restriction-modal-remediation-select"
        variant="outlined"
      >
        <template #selection="{ item }">
          <span
            v-if="item.title"
            :data-testid="`access-restriction-modal-remediation-chip-${item.title}`"
            >{{ $t(`modals.addAccessPermissions.remediation.types.${item.title}`) }}</span
          >
        </template>
        <template #item="{ item, props }">
          <v-list-item
            v-bind="props"
            :title="$t(`modals.addAccessPermissions.remediation.types.${item.title}`)"
            :data-testid="`access-restriction-modal-remediation-${item.title}`"
          >
          </v-list-item>
        </template>
      </v-select>

      <v-divider></v-divider>

      <div class="subtitle2 mt-4">
        {{ $t("modals.addAccessPermissions.assignUsers.title") }}
      </div>

      <button-toggle-group
        v-model="localValue.userType"
        :items="userTypeItems"
        class="d-flex mt-4 mb-6 w-100"
        density="comfortable"
        bg-color="indigo-faint"
      />

      <v-autocomplete
        v-if="isSpecificGroup"
        v-model="localValue.groups"
        class="mt-6"
        :loading="dataLoading"
        :items="groups"
        :label="localValue.groups.length ? $t('modals.addAccessPermissions.group.label') : ''"
        :placeholder="$t('modals.addAccessPermissions.group.placeholder')"
        :rules="[atLeastOneIsRequired()]"
        variant="outlined"
        multiple
        no-filter
        return-object
        :hide-no-data="!groupSearchTerm?.length"
        :no-data-text="$t('modals.addAccessPermissions.group.noData')"
        data-testid="add-access-restrictions-group-autocomplete"
        item-value="groupId"
        item-title="serviceName"
        clear-on-select
        v-model:search="groupSearchTerm"
        @update:search="onGroupsSearchUpdate($event)"
      >
        <template v-slot:chip="{ props, item }">
          <v-chip
            v-bind="props"
            :closable="true"
            variant="flat"
            size="default"
            close-icon="$closeCircle"
            :text="item.title"
            color="indigo-faint"
          >
            <div class="d-flex align-center">
              <coro-icon class="service-icon" :icon-name="item.raw.serviceName"></coro-icon>
              <span class="ml-1 mr-2">{{ item.raw.groupName }}</span>
            </div>
          </v-chip>
        </template>
        <template #item="{ props, item }">
          <v-list-item v-bind="props" title="">
            <div
              class="d-flex align-center"
              :data-testid="`add-access-restrictions-group-${item.raw.name}`"
            >
              <coro-icon class="service-icon" :icon-name="item.raw.serviceName"></coro-icon>
              <span class="ml-1">{{ item.raw.groupName }}</span>
            </div>
          </v-list-item>
        </template>
      </v-autocomplete>
      <v-autocomplete
        v-if="isSpecificLabel"
        v-model="localValue.labels"
        class="mb-3"
        :items="labels"
        :label="$t('general.name')"
        :placeholder="$t('general.name')"
        :rules="[atLeastOneIsRequired()]"
        variant="outlined"
        item-value="id"
        :no-data-text="$t('modals.addAccessPermissions.group.noData')"
        :loading="dataLoading"
        item-title="name"
        clear-on-select
        :custom-filter="() => true"
        return-object
        multiple
        v-model:search="labelSearchTerm"
        @update:search="onLabelsSearchUpdate($event)"
      >
        <template v-slot:chip="{ props, item }">
          <v-chip
            v-bind="props"
            size="default"
            :color="item.raw.color"
            density="compact"
            :text="item.raw.name"
            variant="flat"
            label
            rounded="3"
          >
            <span class="coro-label text-uppercase text-primary" v-text="item.raw.name" />
          </v-chip>
        </template>
        <template #item="{ item, props }">
          <v-list-item v-bind="props" title="">
            <template #prepend>
              <v-checkbox :model-value="isSelected(item.raw)" :ripple="false" />
            </template>
            <div class="d-flex align-center label-popup-block">
              <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
              <span>
                {{ item.raw.name }}
              </span>
            </div>
          </v-list-item>
        </template>
      </v-autocomplete>
      <v-autocomplete
        v-if="isSpecificUser"
        v-model="localValue.users"
        data-testid="add-user-modal-combobox"
        class="mt-3 coro-scrollable-combobox"
        :loading="dataLoading"
        :items="users"
        :label="$t('modals.addAccessPermissions.users.label')"
        :placeholder="$t('modals.addAccessPermissions.users.placeholder')"
        :rules="[atLeastOneIsRequired(), usersRule]"
        variant="outlined"
        multiple
        no-filter
        return-object
        hide-no-data
        item-value="userId"
        item-title="email"
        clear-on-select
        @update:model-value="handleChange()"
        @update:search="onUsersSearchUpdate($event)"
      >
        <template v-slot:chip="{ props, item }">
          <v-chip
            v-bind="props"
            :closable="true"
            variant="flat"
            size="default"
            close-icon="$closeCircle"
            :text="item.title"
            :color="getChipColor(item.raw.email, Patterns.EMAIL)"
          >
            <div class="d-flex align-center justify-space-between">
              <span class="ml-1 mr-2">{{ item.raw.email }}</span>
            </div>
          </v-chip>
        </template>
        <template #item="{ props, item }">
          <v-list-item
            v-bind="props"
            :data-testid="`item-${camelCase(item.raw.email)}`"
            :title="item.raw.email"
          >
          </v-list-item>
        </template>
      </v-autocomplete>
    </v-form>
  </div>
</template>

<script lang="ts">
import Patterns from "@/constants/patterns";
import CoroIcon from "@/components/CoroIcon.vue";
import { computed, type ComputedRef, defineComponent, type PropType, ref, watch } from "vue";
import { AccessPermissionType, Service } from "@/constants/cloud-apps";
import {
  type AccessPermissionItem,
  type AccessPermissionRemediation,
  type MonitoredScopeUser,
  type ThreatTypeItem,
  useAccessPermissionsStore,
} from "@/_store/cloud-apps/access-permissions.module";
import cloneDeep from "lodash/cloneDeep";
import uniqBy from "lodash/uniqBy";
import camelCase from "lodash/camelCase";
import debounce from "lodash/debounce";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { atLeastOneIsRequired, required } from "@/_helpers/validators";
import { useI18n } from "vue-i18n";
import defaultsDeep from "lodash/defaultsDeep";
import type { VuetifyFormRef } from "@/types";
import { TicketTrigger } from "@/constants/tickets.ts";
import { differenceBy } from "lodash";
import type { UserLabel } from "@/_store/users-settings/user-labels.module.ts";
import ButtonToggleGroup, { type ButtonToggleItem } from "@/components/ButtonToggleGroup.vue";

const getDefaultRestriction = (userType: AccessPermissionType) => {
  return {
    userType,
    groups: [],
    users: [],
    labels: [],
    type: null,
    remediation: "none",
  };
};
export default defineComponent({
  components: {
    CoroIcon,
    ButtonToggleGroup,
  },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<ThreatTypeItem>>,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const accessPermissionsStore = useAccessPermissionsStore();
    const groups = ref(props.config.item?.groups?.length ? [...props.config.item.groups] : []);
    const users = ref(props.config.item?.users?.length ? [...props.config.item.users] : []);
    const labels = ref(props.config.item?.labels?.length ? [...props.config.item.labels] : []);

    const valid = ref(true);
    const form = ref<VuetifyFormRef>();
    const dataLoading = ref(false);
    const groupSearchTerm = ref<string | undefined>("");
    const labelSearchTerm = ref<string | undefined>("");
    const localValue = ref<AccessPermissionItem>(
      cloneDeep(
        defaultsDeep(
          props.config.item,
          getDefaultRestriction(props.config.item.userType || AccessPermissionType.ALL_USERS)
        )
      )
    );

    const remediationOptions: ComputedRef<AccessPermissionRemediation[]> = computed(() => {
      return ["none", "suspend", "signin"];
    });

    const userTypes: AccessPermissionType[] = [
      AccessPermissionType.ALL_USERS,
      AccessPermissionType.SPECIFIC_GROUPS,
      AccessPermissionType.SPECIFIC_USERS,
      AccessPermissionType.LABEL,
    ];
    const userTypeItems = computed<ButtonToggleItem[]>(() =>
      userTypes.map((type) => ({
        value: type,
        label: i18n.t(`modals.addAccessPermissions.assignUsers.userType.${type}`),
      }))
    );

    const isSpecificGroup = computed(() => {
      return localValue.value.userType === AccessPermissionType.SPECIFIC_GROUPS;
    });
    const isSpecificUser = computed(() => {
      return localValue.value.userType === AccessPermissionType.SPECIFIC_USERS;
    });
    const isSpecificLabel = computed(() => {
      return localValue.value.userType === AccessPermissionType.LABEL;
    });

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true, immediate: true }
    );

    watch(valid, (value) => {
      if (value === null) {
        return;
      }

      emit("update:valid", value);
    });

    const onUsersSearchUpdate = debounce(async function (email) {
      dataLoading.value = true;

      try {
        users.value = await accessPermissionsStore.searchProtectedUsers({
          email,
          serviceName: "all",
        });
      } catch (e) {
        console.error(e);
      }
      dataLoading.value = false;
    });

    const onLabelsSearchUpdate = debounce(async function (name) {
      if (!name) {
        labels.value = [...localValue.value.labels];
        return;
      }

      dataLoading.value = true;

      try {
        const responseData = await accessPermissionsStore.searchLabels(name);

        labels.value = [
          ...localValue.value.labels,
          ...differenceBy(responseData, localValue.value.labels, "id"),
        ];
      } catch (e) {
        console.error(e);
      }
      dataLoading.value = false;
    }, 500);

    const onGroupsSearchUpdate = debounce(async function (value) {
      dataLoading.value = true;
      const responseData = await accessPermissionsStore.searchGroups({
        searchTerm: value,
        limit: 10,
        service: "all",
      });
      groups.value = uniqBy([...localValue.value.groups, ...responseData], "groupId");
      dataLoading.value = false;
    }, 500);

    const validate = async () => {
      const validationResult = await form.value?.validate();
      return validationResult?.valid;
    };

    const getChipColor = (value: string, pattern: RegExp) => {
      const isValid = pattern.test(value);
      return isValid ? "indigo-faint" : "error";
    };

    const handleChange = () => {
      localValue.value.users = localValue.value.users
        .filter((u: string | MonitoredScopeUser) => !!u)
        .map((user: string | MonitoredScopeUser): MonitoredScopeUser => {
          if ((user as MonitoredScopeUser).email) {
            return user as MonitoredScopeUser;
          }
          return {
            email: user as string,
            service: Service.CORONET,
          };
        });
    };

    const usersRule = (values: MonitoredScopeUser[]) => {
      if (values) {
        if (values.length === 0) return true;

        for (const value of values) {
          if (!Patterns.EMAIL.test(value.email)) {
            return i18n.t("validations.email");
          }
        }
      }
      return true;
    };

    const threatTypeOptions = [
      TicketTrigger.IMPOSSIBLE_TRAVELER,
      TicketTrigger.ABNORMAL_ADMIN_ACTIVITY,
      TicketTrigger.MASS_DELETE,
      TicketTrigger.MASS_DOWNLOAD,
      TicketTrigger.SUSPECTED_BOT_ATTACKS,
      TicketTrigger.SUSPECTED_IDENTITY_COMPROMISE,
    ].map((ticketTrigger) => ({
      name: i18n.t(`modals.addThreatType.threatType.options.${ticketTrigger}.name`),
      description: i18n.t(`modals.addThreatType.threatType.options.${ticketTrigger}.description`),
      value: ticketTrigger,
    }));

    const isSelected = (item: UserLabel) => {
      return localValue.value.labels.some(({ id }) => id === item.id);
    };

    return {
      form,
      localValue,
      valid,
      dataLoading,
      remediationOptions,
      isSpecificGroup,
      isSpecificUser,
      isSpecificLabel,
      groupSearchTerm,
      labelSearchTerm,
      groups,
      users,
      Patterns,
      camelCase,
      handleChange,
      getChipColor,
      usersRule,
      required,
      atLeastOneIsRequired,
      validate,
      onGroupsSearchUpdate,
      onUsersSearchUpdate,
      onLabelsSearchUpdate,
      threatTypeOptions,
      userTypeItems,
      isSelected,
      labels,
    };
  },
});
</script>

<style lang="scss" scoped>
.restriction-select {
  width: 252px;
}
:deep(*) {
  .v-field .v-chip {
    --v-chip-height: 32px;
  }
  .service-icon {
    height: 24px;
    width: 24px;
  }

  .v-btn.v-btn--active {
    background-color: rgb(var(--v-theme-white)) !important;
    font-weight: 600;

    .v-btn__overlay {
      opacity: 0 !important;
    }
  }
}

.service-icon {
  width: 24px;
  height: 24px;
}

.time-select {
  max-width: 349px;
}

.coro-label {
  font-size: 14px;
  letter-spacing: 1px;
  color: rgb(var(--v-theme-indigo-base));
}
</style>
